import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useGetAllBranchesQuery } from "../../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../../toolkitServices/categoryApi";

const defaultTemplate = {
  name: "",
  templateType: "prn",
  branchIds: [],
  categoryIds: [],
  appliesToAllBranches: false,
  appliesToAllCategories: false,
  prnCode: "",
};

const samplePrn = `^XA
^PW400
^LL200
^FO20,20^A0N,30,30^FDProduct: Ring^FS
^FO20,60^A0N,28,28^FDWt: 12.500 g^FS
^FO20,100^BY2
^BCN,60,Y,N,N
^FD123456789012^FS
^XZ`;

const normalizeList = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.items)) return res.items;
  if (Array.isArray(res.data)) return res.data;
  if (Array.isArray(res.rows)) return res.rows;
  if (Array.isArray(res.results)) return res.results;
  return [];
};

const getId = (item) => item?._id || item?.id || item?.value || item?.Id;

const getBranchLabel = (item) =>
  item?.name ||
  item?.branchName ||
  item?.BranchName ||
  item?.label ||
  "Unnamed";

const getCategoryLabel = (item) =>
  item?.name ||
  item?.categoryName ||
  item?.CategoryName ||
  item?.label ||
  "Unnamed";

const extractPreviewLines = (code = "") => {
  return code
    .split("\n")
    .map((line) => {
      const fdIndex = line.indexOf("^FD");
      const fsIndex = line.indexOf("^FS");

      if (fdIndex === -1 || fsIndex === -1 || fsIndex <= fdIndex) return null;

      const value = line.slice(fdIndex + 3, fsIndex).trim();

      let type = "text";
      if (line.includes("^BC")) type = "barcode";
      if (line.includes("^BQN")) type = "qrcode";

      return { type, value };
    })
    .filter(Boolean);
};

const quickSnippets = [
  {
    label: "+ Text",
    className: "bg-indigo-600 hover:bg-indigo-700 text-white",
    code: "^FO20,20^A0N,30,30^FDText Here^FS",
  },
  {
    label: "+ Barcode",
    className: "bg-emerald-600 hover:bg-emerald-700 text-white",
    code: "^FO20,100^BY2\n^BCN,60,Y,N,N\n^FD123456789012^FS",
  },
  {
    label: "+ QR",
    className: "bg-blue-600 hover:bg-blue-700 text-white",
    code: "^FO260,20^BQN,2,4^FDQA,123456789012^FS",
  },
];

const PrnLabelTemplateEditor = ({
  initialTemplate,
  onSave,
  isSaving = false,
}) => {
  const navigate = useNavigate();
  const textareaRef = useRef(null);

  const mergedInitial = useMemo(() => {
    return {
      ...defaultTemplate,
      ...(initialTemplate || {}),
      templateType: "prn",
      branchIds: initialTemplate?.branchIds || [],
      categoryIds: initialTemplate?.categoryIds || [],
      appliesToAllBranches: initialTemplate?.appliesToAllBranches || false,
      appliesToAllCategories: initialTemplate?.appliesToAllCategories || false,
    };
  }, [initialTemplate]);

  const [template, setTemplate] = useState(mergedInitial);
  const [branchOpen, setBranchOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [mobileTab, setMobileTab] = useState("editor");

  useEffect(() => {
    setTemplate(mergedInitial);
  }, [mergedInitial]);

  const {
    data: branchRes,
    isLoading: branchesLoading,
    isError: branchesError,
  } = useGetAllBranchesQuery({ page: 1, limit: 1000, active: true });

  const {
    data: categoryRes,
    isLoading: categoriesLoading,
    isError: categoriesError,
  } = useGetAllCategoriesQuery({ page: 1, limit: 1000, active: true });

  const branches = useMemo(() => normalizeList(branchRes), [branchRes]);
  const categories = useMemo(() => normalizeList(categoryRes), [categoryRes]);

  const previewLines = useMemo(
    () => extractPreviewLines(template.prnCode || ""),
    [template.prnCode],
  );

  const hasBranchSelection = template.appliesToAllBranches
    ? true
    : (template.branchIds || []).length > 0;

  const handleChange = (key, value) => {
    setTemplate((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const insertAtCursor = (snippet) => {
    const textarea = textareaRef.current;

    if (!textarea) {
      setTemplate((prev) => ({
        ...prev,
        prnCode: `${prev.prnCode || ""}\n${snippet}`.trim(),
      }));
      return;
    }

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const current = template.prnCode || "";

    const before = current.slice(0, start);
    const after = current.slice(end);

    const prefix = before && !before.endsWith("\n") ? "\n" : "";
    const suffix = after && !after.startsWith("\n") ? "\n" : "";

    const nextValue = `${before}${prefix}${snippet}${suffix}${after}`;

    setTemplate((prev) => ({
      ...prev,
      prnCode: nextValue,
    }));

    requestAnimationFrame(() => {
      const pos = (before + prefix + snippet).length;
      textarea.focus();
      textarea.setSelectionRange(pos, pos);
    });
  };

  const handleUseSample = () => {
    setTemplate((prev) => ({
      ...prev,
      prnCode: samplePrn,
    }));
    toast.success("Sample PRN inserted");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(template.prnCode || "");
      toast.success("PRN copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const handleClear = () => {
    setTemplate((prev) => ({
      ...prev,
      prnCode: "",
    }));
    toast.success("Editor cleared");
  };

  const toggleBranch = (id) => {
    setTemplate((prev) => {
      if (prev.appliesToAllBranches) return prev;

      const existing = prev.branchIds || [];
      const next = existing.includes(id)
        ? existing.filter((x) => x !== id)
        : [...existing, id];

      return { ...prev, branchIds: next };
    });
  };

  const toggleCategory = (id) => {
    setTemplate((prev) => {
      if (prev.appliesToAllCategories) return prev;

      const existing = prev.categoryIds || [];
      const next = existing.includes(id)
        ? existing.filter((x) => x !== id)
        : [...existing, id];

      return { ...prev, categoryIds: next };
    });
  };

  const selectAllBranches = () => {
    const allIds = branches.map((item) => getId(item)).filter(Boolean);
    setTemplate((prev) => ({
      ...prev,
      appliesToAllBranches: false,
      branchIds: allIds,
    }));
  };

  const clearBranches = () => {
    setTemplate((prev) => ({
      ...prev,
      appliesToAllBranches: false,
      branchIds: [],
    }));
  };

  const selectAllCategories = () => {
    const allIds = categories.map((item) => getId(item)).filter(Boolean);
    setTemplate((prev) => ({
      ...prev,
      appliesToAllCategories: false,
      categoryIds: allIds,
    }));
  };

  const clearCategories = () => {
    setTemplate((prev) => ({
      ...prev,
      appliesToAllCategories: false,
      categoryIds: [],
    }));
  };

  const handleSubmit = () => {
    const payload = {
      ...template,
      templateType: "prn",
      branchIds: template.appliesToAllBranches ? [] : template.branchIds || [],
      categoryIds: template.appliesToAllCategories
        ? []
        : template.categoryIds || [],
      prnCode: (template.prnCode || "").trim(),
      name: (template.name || "").trim(),
    };

    if (!payload.name) {
      toast.error("Template name is required");
      return;
    }

    if (!payload.prnCode) {
      toast.error("PRN code is required");
      return;
    }

    onSave?.(payload);
  };

  const renderTemplateSettings = () => (
    <div className="space-y-4">
      <div>
        <label className="mb-1.5 block text-[11px] font-semibold text-slate-700">
          Template Name
        </label>
        <input
          type="text"
          value={template.name}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="Enter template name"
          className="w-full rounded-xl border border-slate-300 px-3 py-2.5 text-[12px] outline-none transition focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10"
        />
      </div>

      <div>
        <label className="mb-1.5 block text-[11px] font-semibold text-slate-700">
          Template Type
        </label>
        <input
          type="text"
          value="PRN"
          disabled
          className="w-full cursor-not-allowed rounded-xl border border-slate-200 bg-slate-100 px-3 py-2.5 text-[12px] text-slate-500"
        />
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <label className="text-[11px] font-semibold text-slate-700">
            Branches
          </label>

          <label className="flex items-center gap-1 text-[10px] text-slate-500">
            <input
              type="checkbox"
              className="accent-violet-600"
              checked={template.appliesToAllBranches}
              onChange={(e) =>
                setTemplate((prev) => ({
                  ...prev,
                  appliesToAllBranches: e.target.checked,
                  branchIds: e.target.checked ? [] : prev.branchIds,
                }))
              }
            />
            <span>All</span>
          </label>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => setBranchOpen((prev) => !prev)}
            disabled={template.appliesToAllBranches}
            className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-[12px] ${
              template.appliesToAllBranches
                ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span className="truncate">
              {branchesLoading
                ? "Loading branches..."
                : template.appliesToAllBranches
                  ? "All branches"
                  : template.branchIds?.length
                    ? `${template.branchIds.length} branch selected`
                    : "Select branch(es)"}
            </span>
            <span className="ml-2 text-[10px]">{branchOpen ? "▲" : "▼"}</span>
          </button>

          {branchOpen && !template.appliesToAllBranches && (
            <div className="absolute z-40 mt-2 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
              {branchesError && (
                <div className="px-3 py-2 text-[11px] text-red-500">
                  Error loading branches
                </div>
              )}

              {!branchesLoading && !branchesError && branches.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-slate-500">
                  No branches found
                </div>
              )}

              {!branchesLoading && branches.length > 0 && (
                <>
                  <div className="flex justify-between border-b border-slate-100 bg-slate-50 px-3 py-2 text-[10px]">
                    <button
                      type="button"
                      onClick={selectAllBranches}
                      className="text-slate-600 hover:text-slate-900"
                    >
                      Select all
                    </button>
                    <button
                      type="button"
                      onClick={clearBranches}
                      className="text-slate-600 hover:text-slate-900"
                    >
                      Clear
                    </button>
                  </div>

                  {branches.map((item) => {
                    const id = getId(item);
                    if (!id) return null;

                    const checked = (template.branchIds || []).includes(id);

                    return (
                      <label
                        key={id}
                        className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[11px] text-slate-700 hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          className="accent-violet-600"
                          checked={checked}
                          onChange={() => toggleBranch(id)}
                        />
                        <span className="truncate">{getBranchLabel(item)}</span>
                      </label>
                    );
                  })}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="mb-1.5 flex items-center justify-between">
          <label className="text-[11px] font-semibold text-slate-700">
            Categories
          </label>

          <label className="flex items-center gap-1 text-[10px] text-slate-500">
            <input
              type="checkbox"
              className="accent-violet-600"
              checked={template.appliesToAllCategories}
              onChange={(e) =>
                setTemplate((prev) => ({
                  ...prev,
                  appliesToAllCategories: e.target.checked,
                  categoryIds: e.target.checked ? [] : prev.categoryIds,
                }))
              }
            />
            <span>All</span>
          </label>
        </div>

        <div className="relative">
          <button
            type="button"
            onClick={() => {
              if (!hasBranchSelection) return;
              setCategoryOpen((prev) => !prev);
            }}
            disabled={!hasBranchSelection || template.appliesToAllCategories}
            className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-[12px] ${
              !hasBranchSelection || template.appliesToAllCategories
                ? "cursor-not-allowed border-slate-200 bg-slate-50 text-slate-400"
                : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
            }`}
          >
            <span className="truncate">
              {!hasBranchSelection
                ? "Select branch first"
                : template.appliesToAllCategories
                  ? "All categories"
                  : categoriesLoading
                    ? "Loading categories..."
                    : template.categoryIds?.length
                      ? `${template.categoryIds.length} category selected`
                      : "Select category(es)"}
            </span>

            <span className="ml-2 text-[10px]">{categoryOpen ? "▲" : "▼"}</span>
          </button>

          {categoryOpen &&
            hasBranchSelection &&
            !template.appliesToAllCategories && (
              <div className="absolute z-40 mt-2 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl">
                {categoriesError && (
                  <div className="px-3 py-2 text-[11px] text-red-500">
                    Error loading categories
                  </div>
                )}

                {!categoriesLoading &&
                  !categoriesError &&
                  categories.length === 0 && (
                    <div className="px-3 py-2 text-[11px] text-slate-500">
                      No categories found
                    </div>
                  )}

                {!categoriesLoading && categories.length > 0 && (
                  <>
                    <div className="flex justify-between border-b border-slate-100 bg-slate-50 px-3 py-2 text-[10px]">
                      <button
                        type="button"
                        onClick={selectAllCategories}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        Select all
                      </button>
                      <button
                        type="button"
                        onClick={clearCategories}
                        className="text-slate-600 hover:text-slate-900"
                      >
                        Clear
                      </button>
                    </div>

                    {categories.map((item) => {
                      const id = getId(item);
                      if (!id) return null;

                      const checked = (template.categoryIds || []).includes(id);

                      return (
                        <label
                          key={id}
                          className="flex cursor-pointer items-center gap-2 px-3 py-2 text-[11px] text-slate-700 hover:bg-slate-50"
                        >
                          <input
                            type="checkbox"
                            className="accent-violet-600"
                            checked={checked}
                            onChange={() => toggleCategory(id)}
                          />
                          <span className="truncate">
                            {getCategoryLabel(item)}
                          </span>
                        </label>
                      );
                    })}
                  </>
                )}
              </div>
            )}
        </div>

        <p className="mt-1 text-[10px] text-slate-500">
          Selected branches ke andar selected categories pe template apply hoga.
        </p>
      </div>
    </div>
  );

  const renderQuickTools = () => (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {quickSnippets.map((item) => (
          <button
            key={item.label}
            type="button"
            onClick={() => insertAtCursor(item.code)}
            className={`rounded-xl px-3 py-2 text-[12px] font-semibold transition ${item.className}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={handleUseSample}
          className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-[12px] font-semibold text-slate-700 transition hover:bg-slate-100"
        >
          Use Sample
        </button>

        <button
          type="button"
          onClick={handleClear}
          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-[12px] font-semibold text-red-600 transition hover:bg-red-100"
        >
          Clear
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-[10px] text-slate-500">
        <div className="mb-1 font-semibold text-slate-700">Tips</div>
        <ul className="list-inside list-disc space-y-1">
          <li>Paste ready PRN directly and save.</li>
          <li>No width / height setup needed here.</li>
          <li>Printer settings PRN code ke andar hi rahenge.</li>
        </ul>
      </div>
    </div>
  );

  const renderPreview = () => (
    <div className="space-y-3">
      <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] text-slate-500">
        Basic preview only. Actual print output printer pe depend karega.
      </div>

      {previewLines.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-[11px] text-slate-500">
          No previewable lines found.
        </div>
      ) : (
        previewLines.map((item, index) => (
          <div
            key={`${item.type}-${index}`}
            className="rounded-xl border border-slate-200 bg-slate-50 p-3"
          >
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {item.type}
            </div>
            <div className="break-all rounded bg-white px-3 py-2 text-[12px] text-slate-800">
              {item.value}
            </div>
          </div>
        ))
      )}
    </div>
  );

  return (
    <div className="px-3 pb-4 sm:px-4 md:px-6">
      <div className="mx-auto flex h-[calc(100vh-120px)] min-h-[560px] max-w-7xl flex-col gap-3 md:h-[calc(100vh-90px)]">
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold leading-tight text-[#4b3d91] sm:text-xl md:text-2xl">
              PRN Label Template Editor
            </h1>
            <p className="mt-1 text-[11px] text-slate-500 sm:text-xs">
              Paste PRN code and save
            </p>
          </div>

          <div className="flex flex-shrink-0 items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={() => navigate("/settings/prn-label-templates")}
              className="rounded-xl border border-slate-300 px-3 py-2 text-[12px] font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              Back
            </button>

            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className={`rounded-xl px-4 py-2 text-[12px] font-semibold text-white shadow-sm transition ${
                isSaving
                  ? "cursor-wait bg-violet-400"
                  : "bg-violet-600 hover:bg-violet-700"
              }`}
            >
              {isSaving ? "Saving..." : "Save Template"}
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-md">
          <div className="h-full min-h-0 overflow-hidden flex flex-col lg:grid lg:grid-cols-[300px_minmax(0,1fr)_340px]">
            <aside className="hidden min-h-0 flex-col border-r border-slate-200 bg-white lg:flex">
              <div className="border-b border-slate-200 bg-white px-4 py-3">
                <div className="text-[12px] font-semibold text-slate-800">
                  Template & Tools
                </div>
                <div className="text-[10px] text-slate-500">
                  Scope + quick PRN snippets
                </div>
              </div>

              <div className="flex-1 space-y-4 overflow-y-auto p-4">
                {renderTemplateSettings()}
                {renderQuickTools()}
              </div>
            </aside>

            <main className="flex min-h-0 flex-col overflow-hidden bg-white">
              <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2 sm:px-4">
                <div className="truncate text-[10px] text-slate-600 sm:text-[11px]">
                  Paste raw PRN exactly as provided by printer software
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleCopy}
                    className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Copy
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto bg-slate-950 p-3 sm:p-4">
                <textarea
                  ref={textareaRef}
                  value={template.prnCode}
                  onChange={(e) => handleChange("prnCode", e.target.value)}
                  placeholder="Paste PRN code here..."
                  spellCheck={false}
                  className="min-h-full w-full resize-none rounded-2xl border border-slate-700 bg-slate-950 p-4 font-mono text-sm text-green-300 outline-none transition focus:border-violet-500"
                />
              </div>

              <div className="flex items-center gap-2 overflow-x-auto border-t border-slate-200 bg-white px-3 py-2 lg:hidden">
                <button
                  type="button"
                  onClick={() => setMobileTab("template")}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700"
                >
                  Template
                </button>
                <button
                  type="button"
                  onClick={() => setMobileTab("editor")}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700"
                >
                  Tools
                </button>
                <button
                  type="button"
                  onClick={() => setMobileTab("preview")}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700"
                >
                  Preview
                </button>
              </div>
            </main>

            <aside className="hidden min-h-0 flex-col border-l border-slate-200 bg-white lg:flex">
              <div className="border-b border-slate-200 bg-white px-4 py-3">
                <div className="text-[12px] font-semibold text-slate-800">
                  Preview
                </div>
                <div className="text-[10px] text-slate-500">
                  Basic extracted display
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {renderPreview()}
              </div>
            </aside>

            <section className="flex h-[42vh] min-h-[260px] flex-col overflow-hidden border-t border-slate-200 bg-white lg:hidden">
              <div className="flex items-center gap-2 border-b bg-slate-50 px-2 py-2">
                {[
                  { key: "editor", label: "Tools" },
                  { key: "preview", label: "Preview" },
                  { key: "template", label: "Template" },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setMobileTab(item.key)}
                    className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold transition ${
                      mobileTab === item.key
                        ? "border-violet-600 bg-violet-600 text-white"
                        : "border-slate-200 bg-white text-slate-700"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                {mobileTab === "editor" && renderQuickTools()}
                {mobileTab === "template" && renderTemplateSettings()}
                {mobileTab === "preview" && renderPreview()}
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrnLabelTemplateEditor;
